import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Email,
  WhatsApp,
  TrendingUp,
  TrendingDown,
  Refresh
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getCommunicationStats } from '../../redux/actions/communicationActions';

const CommunicationAnalytics = () => {
  const dispatch = useDispatch();
  const { communicationStats, loading, error } = useSelector((state) => state.communication);
  
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    await dispatch(getCommunicationStats(startDate, endDate));
  };

  const handleDateChange = () => {
    if (startDate && endDate) {
      loadStats();
    }
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    const emailStats = communicationStats.filter(stat => stat.type === 'email');
    const whatsappStats = communicationStats.filter(stat => stat.type === 'whatsapp');
    
    const totalEmailsSent = emailStats.reduce((sum, stat) => sum + (stat.count || 0), 0);
    const totalWhatsAppSent = whatsappStats.reduce((sum, stat) => sum + (stat.count || 0), 0);
    
    const emailsDelivered = emailStats.find(stat => stat.status === 'delivered')?.count || 0;
    const whatsappDelivered = whatsappStats.find(stat => stat.status === 'delivered')?.count || 0;
    
    const emailsFailed = emailStats.find(stat => stat.status === 'failed')?.count || 0;
    const whatsappFailed = whatsappStats.find(stat => stat.status === 'failed')?.count || 0;

    return {
      totalEmailsSent,
      totalWhatsAppSent,
      emailsDelivered,
      whatsappDelivered,
      emailsFailed,
      whatsappFailed,
      emailDeliveryRate: totalEmailsSent > 0 ? ((emailsDelivered / totalEmailsSent) * 100).toFixed(1) : 0,
      whatsappDeliveryRate: totalWhatsAppSent > 0 ? ((whatsappDelivered / totalWhatsAppSent) * 100).toFixed(1) : 0
    };
  };

  const summaryStats = getSummaryStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Communication Analytics</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadStats}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Date Range Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Date Range
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleDateChange}
                disabled={!startDate || !endDate}
              >
                Update Analytics
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Emails Sent
                  </Typography>
                  <Typography variant="h4">
                    {summaryStats.totalEmailsSent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Delivery Rate: {summaryStats.emailDeliveryRate}%
                  </Typography>
                </Box>
                <Email color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    WhatsApp Sent
                  </Typography>
                  <Typography variant="h4">
                    {summaryStats.totalWhatsAppSent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Delivery Rate: {summaryStats.whatsappDeliveryRate}%
                  </Typography>
                </Box>
                <WhatsApp color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Delivered
                  </Typography>
                  <Typography variant="h4">
                    {summaryStats.emailsDelivered + summaryStats.whatsappDelivered}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                    Successful
                  </Typography>
                </Box>
                <TrendingUp color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Failed
                  </Typography>
                  <Typography variant="h4">
                    {summaryStats.emailsFailed + summaryStats.whatsappFailed}
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    <TrendingDown sx={{ fontSize: 16, mr: 0.5 }} />
                    Failed
                  </Typography>
                </Box>
                <TrendingDown color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Statistics Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Statistics
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Count</TableCell>
                  <TableCell>Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {communicationStats.map((stat, index) => {
                  const totalForType = communicationStats
                    .filter(s => s.type === stat.type)
                    .reduce((sum, s) => sum + (s.count || 0), 0);
                  const percentage = totalForType > 0 ? ((stat.count / totalForType) * 100).toFixed(1) : 0;
                  
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {stat.type === 'email' ? <Email /> : <WhatsApp />}
                          <Typography sx={{ ml: 1, textTransform: 'capitalize' }}>
                            {stat.type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={stat.status}
                          color={
                            stat.status === 'delivered' ? 'success' :
                            stat.status === 'sent' ? 'info' :
                            stat.status === 'failed' ? 'error' :
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {stat.count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {percentage}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {communicationStats.length === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No communication data found for the selected date range
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try selecting a different date range or send some communications to see analytics
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CommunicationAnalytics;
